/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ADF_DATE_FORMATS, AdfDateFnsAdapter, ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { AlfrescoApiService, ContentService, RenditionService, SharedLinksApiService } from '@alfresco/adf-content-services';
import { NodeEntry, NodesApi } from '@alfresco/js-api';
import { MailService } from '../../services/mail.service';
import { add, endOfDay, format, isBefore } from 'date-fns';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface ShareLinkDialogData {
  node: NodeEntry;
  baseShareUrl: string;
}

interface ShareMailTemplate {
  id: string;
  name: string;
  subject: string;
}

/**
 * Replacement for ADF's `adf-share-dialog` with a redesigned layout and a
 * "send by email" section. Link creation/expiry behaviour mirrors ADF: the link
 * is created on open when missing, and setting an expiry date recreates the
 * link server-side (which also changes the URL).
 *
 * Email sending uses a `mailto:` draft filled from the administrator-managed
 * templates under "/Company Home/Email Templates" — the same nodes the
 * document-expiry job uses. Placeholders `{name} {path} {expiryDate} {owner}
 * {modifier} {link}` are substituted before the mail app opens.
 */
@Component({
  selector: 'aca-share-link-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: ADF_DATE_FORMATS },
    { provide: DateAdapter, useClass: AdfDateFnsAdapter }
  ],
  templateUrl: './share-link-dialog.component.html',
  styleUrls: ['./share-link-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'aca-sharelink' }
})
export class ShareLinkDialogComponent implements OnInit {
  /** What the jobs service's mail relay refuses, by its error code. */
  private static readonly MAIL_ERRORS: Record<string, string> = {
    'recipient-not-allowed': 'DMS.SHARELINK.MAIL_NOT_ALLOWED',
    'invalid-recipient': 'DMS.SHARELINK.MAIL_INVALID',
    'too-many-recipients': 'DMS.SHARELINK.MAIL_TOO_MANY',
    'rate-limited': 'DMS.SHARELINK.MAIL_RATE_LIMITED'
  };

  private readonly sharedLinksApi = inject(SharedLinksApiService);
  private readonly dialogRef = inject(MatDialogRef<ShareLinkDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly contentService = inject(ContentService);
  private readonly renditionService = inject(RenditionService);
  private readonly notifications = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly api = inject(AlfrescoApiService);
  private readonly destroyRef = inject(DestroyRef);
  readonly mail = inject(MailService);

  readonly data: ShareLinkDialogData = inject(MAT_DIALOG_DATA);

  fileName = '';
  sharedId = '';
  shareUrl = '';
  isFileShared = false;
  isBusy = false;
  copied = false;

  readonly minDate = add(new Date(), { days: 1 });
  expiryOn = false;
  readonly expiryDate = new FormControl<Date | null>({ value: null, disabled: true }, [
    Validators.required,
    (control) => (isBefore(endOfDay(new Date(control.value)), this.minDate) ? { invalidDate: true } : null)
  ]);

  mailTo = '';
  mailSubject = '';
  mailBody = '';
  mailInvalid = false;
  mailSending = false;
  mailSent = false;
  templates: ShareMailTemplate[] = [];
  templatesLoaded = false;
  selectedTemplateId = '';

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  ngOnInit() {
    const entry = this.data.node?.entry;
    if (!entry) {
      return;
    }
    this.mail.load();
    this.fileName = entry.name;
    const properties = entry.properties || {};
    if (properties['qshare:sharedId']) {
      this.sharedId = properties['qshare:sharedId'];
      this.isFileShared = true;
      const expiry = properties['qshare:expiryDate'];
      this.expiryOn = !!expiry;
      this.expiryDate.setValue(expiry ? new Date(expiry) : null, { emitEvent: false });
      if (this.expiryOn) {
        this.expiryDate.enable({ emitEvent: false });
      }
      this.shareUrl = `${this.data.baseShareUrl}${this.sharedId}`;
      this.loadTemplates();
      this.resetMailDraft();
    } else {
      this.createLink(null);
    }
  }

  get canUpdate(): boolean {
    const entry = this.data.node?.entry;
    if (entry?.allowableOperations) {
      return this.contentService.hasAllowableOperations(entry, 'update');
    }
    return true;
  }

  /** Enable/disable the public link. */
  toggleShare(event: MatSlideToggleChange) {
    if (event.checked) {
      this.createLink(null);
    } else {
      this.confirmUnshare();
    }
  }

  /** Turning expiry on/off (or changing the date) recreates the link server-side. */
  toggleExpiry(event: MatSlideToggleChange) {
    this.expiryOn = event.checked;
    if (event.checked) {
      this.expiryDate.enable();
    } else {
      this.expiryDate.disable();
      this.expiryDate.setValue(null);
      this.isBusy = true;
      this.sharedLinksApi.deleteSharedLink(this.sharedId).subscribe((response) => {
        if (response instanceof Error) {
          this.isBusy = false;
          this.showError(response);
        } else {
          this.setEntryExpiry(null);
          this.expiryOn = false;
          this.createLink(null);
        }
      });
    }
  }

  expiryChanged() {
    if (!this.expiryDate.valid || !this.expiryDate.value) {
      return;
    }
    const expiresAt = endOfDay(this.expiryDate.value);
    this.isBusy = true;
    this.sharedLinksApi.deleteSharedLink(this.sharedId).subscribe((response) => {
      if (response instanceof Error) {
        this.isBusy = false;
        this.showError(response);
      } else {
        this.setEntryExpiry(this.expiryDate.value);
        this.createLink(expiresAt);
      }
    });
  }

  copyLink() {
    const done = () => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.shareUrl).then(done, done);
    } else {
      const scratch = document.createElement('textarea');
      scratch.value = this.shareUrl;
      document.body.appendChild(scratch);
      scratch.select();
      document.execCommand('copy');
      scratch.remove();
      done();
    }
  }

  /** Loads the administrator-managed templates from "/Company Home/Email Templates". */
  loadTemplates() {
    from(this.nodes.getNode('-root-', { relativePath: 'Email Templates' }) as Promise<any>)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((res: any) => res.entry.id as string),
        switchMap((fid) => from(this.nodes.listNodeChildren(fid, { include: ['properties'], maxItems: 200 } as any) as Promise<any>)),
        map((res: any) =>
          (res?.list?.entries || [])
            .map((e: any) => e.entry)
            .filter((n: any) => n.isFile)
            .map((n: any): ShareMailTemplate => ({ id: n.id, name: n.name, subject: n.properties?.['cm:title'] || '' }))
        ),
        catchError(() => of([] as ShareMailTemplate[]))
      )
      .subscribe((list) => {
        this.templates = list;
        this.templatesLoaded = true;
      });
  }

  /** Fills subject/body from a picked template, substituting share placeholders. */
  applyTemplate(id: string) {
    this.selectedTemplateId = id;
    if (!id) {
      this.resetMailDraft();
      return;
    }
    const tpl = this.templates.find((t) => t.id === id);
    if (!tpl) {
      return;
    }
    from(this.nodes.getNodeContent(id) as Promise<any>)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? '')))),
        catchError(() => of(''))
      )
      .subscribe((body) => {
        this.mailSubject = this.fillPlaceholders(tpl.subject || '');
        this.mailBody = this.fillPlaceholders(body);
      });
  }

  /** Default draft used when no template is selected. */
  resetMailDraft() {
    this.mailSubject = this.translate.instant('DMS.SHARELINK.MAIL_SUBJECT_DEFAULT', { name: this.fileName });
    this.mailBody = this.translate.instant('DMS.SHARELINK.MAIL_BODY_DEFAULT', {
      name: this.fileName,
      link: this.shareUrl,
      expiry: this.expiryLabel
    });
  }

  /**
   * Sends through the channel the administrator configured (Alfresco mail
   * action, EmailJS, or an HTTP relay). With `mailto` configured — or no
   * configuration at all — it opens the draft in the user's own mail app.
   */
  sendMail() {
    const recipients = this.mailTo
      .split(/[,;\s]+/)
      .map((r) => r.trim())
      .filter((r) => !!r);
    this.mailInvalid = recipients.some((r) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r));
    if (this.mailInvalid) {
      return;
    }

    const request = {
      to: recipients.join(','),
      subject: this.mailSubject,
      body: this.mailBody,
      vars: { link: this.shareUrl, name: this.fileName, nodeId: this.data.node.entry.id }
    };

    if (this.mail.settings().mode === 'mailto') {
      const to = encodeURIComponent(request.to);
      const subject = encodeURIComponent(request.subject);
      const body = encodeURIComponent(request.body.replace(/\r?\n/g, '\r\n'));
      this.openMailto(`mailto:${to}?subject=${subject}&body=${body}`);
      return;
    }

    this.mailSending = true;
    this.mail.send(request).subscribe({
      next: () => {
        this.mailSending = false;
        this.mailSent = true;
        this.notifications.showInfo('DMS.SHARELINK.MAIL_SENT');
        setTimeout(() => (this.mailSent = false), 4000);
      },
      error: (err) => {
        this.mailSending = false;
        this.notifications.showError(ShareLinkDialogComponent.MAIL_ERRORS[err?.error?.code] ?? 'DMS.SHARELINK.MAIL_SEND_FAILED');
      }
    });
  }

  /** `mailto` opens a local draft; every other mode sends straight away. */
  get sendsDirectly(): boolean {
    return this.mail.settings().mode !== 'mailto';
  }

  /** Isolated so specs can spy on it — assigning location.href navigates the test browser otherwise. */
  private openMailto(href: string) {
    window.location.href = href;
  }

  close() {
    this.dialogRef.close(this.isFileShared);
  }

  private get expiryLabel(): string {
    return this.expiryOn && this.expiryDate.value ? format(this.expiryDate.value, 'yyyy-MM-dd') : this.translate.instant('DMS.SHARELINK.NO_EXPIRY');
  }

  private fillPlaceholders(text: string): string {
    const entry = this.data.node?.entry;
    return (text || '')
      .replace(/\{name\}/g, this.fileName)
      .replace(/\{path\}/g, entry?.path?.name || '')
      .replace(/\{expiryDate\}/g, this.expiryLabel)
      .replace(/\{owner\}/g, entry?.createdByUser?.displayName || '')
      .replace(/\{modifier\}/g, entry?.modifiedByUser?.displayName || '')
      .replace(/\{link\}/g, this.shareUrl);
  }

  private createLink(expiresAt: Date | null) {
    const previousUrl = this.shareUrl;
    this.isBusy = true;
    this.sharedLinksApi.createSharedLinks(this.data.node.entry.id, expiresAt ? { nodeId: this.data.node.entry.id, expiresAt } : undefined).subscribe(
      (sharedLink) => {
        if (sharedLink.entry) {
          this.sharedId = sharedLink.entry.id;
          const entry = this.data.node.entry;
          entry.properties = { ...(entry.properties || {}), 'qshare:sharedId': this.sharedId };
          this.shareUrl = `${this.data.baseShareUrl}${this.sharedId}`;
          this.isFileShared = true;
          this.isBusy = false;
          void this.renditionService.getNodeRendition(entry.id);
          this.loadTemplates();
          // Keep the draft in sync so {link} always carries the newest URL.
          if (this.selectedTemplateId) {
            this.applyTemplate(this.selectedTemplateId);
          } else if (this.mailBody && previousUrl && this.mailBody.includes(previousUrl)) {
            this.mailBody = this.mailBody.split(previousUrl).join(this.shareUrl);
          } else if (!this.mailBody) {
            this.resetMailDraft();
          }
        }
      },
      () => {
        this.isBusy = false;
        this.isFileShared = false;
        this.notifications.showError('DMS.SHARELINK.CREATE_ERROR');
      }
    );
  }

  private confirmUnshare() {
    this.isFileShared = true; // keep the toggle on until the user confirms
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'SHARE.CONFIRMATION.DIALOG-TITLE',
          message: 'SHARE.CONFIRMATION.MESSAGE',
          yesLabel: 'SHARE.CONFIRMATION.REMOVE',
          noLabel: 'SHARE.CONFIRMATION.CANCEL'
        },
        minWidth: '250px',
        closeOnNavigation: true
      })
      .beforeClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.unshare();
        }
      });
  }

  private unshare() {
    this.isBusy = true;
    this.sharedLinksApi.deleteSharedLink(this.sharedId).subscribe((response) => {
      if (response instanceof Error) {
        this.isBusy = false;
        this.showError(response);
      } else {
        const props = this.data.node.entry.properties;
        if (props) {
          props['qshare:sharedId'] = null;
          props['qshare:expiryDate'] = null;
        }
        this.isBusy = false;
        this.isFileShared = false;
        this.sharedId = '';
        this.shareUrl = '';
        this.expiryOn = false;
        this.expiryDate.disable();
        this.expiryDate.setValue(null);
      }
    });
  }

  private setEntryExpiry(date: Date | null) {
    const props = this.data.node.entry.properties;
    if (props) {
      props['qshare:expiryDate'] = date ? new Date(date) : null;
    }
  }

  private showError(error: Error) {
    let statusCode = 0;
    try {
      statusCode = JSON.parse(error.message).error.statusCode;
    } catch {
      /* not a JSON error */
    }
    this.notifications.showError(statusCode === 403 ? 'SHARE.UNSHARE_PERMISSION_ERROR' : 'SHARE.UNSHARE_ERROR');
  }
}
